import { ReactElement, SetStateAction, useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    ExpandMoreIcon,
    MonitoringLevelIcon,
    TuneIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import ActionToolbarButtonWrapper from "components/DesignComponents/Button/ActionToolbarButtonWrapper";
import ListChoice from "components/DesignComponents/ListChoice";
import Menu, {
    centeredAnchorOrigin,
    centeredTransformOrigin
} from "components/DesignComponents/Menu";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    isClientTypeCompany,
    isPremiumLevel,
    MenuAnchorEl,
    useMediaQueries,
    usePermissions
} from "shared";

import CompanyLevelManualChange from "../dialogs/Companies/CompanyLevelManualChange";
import CreateCompany from "../dialogs/Companies/CreateCompany";
import CustomResourceLimits from "../dialogs/Companies/CustomResourceLimits";
import TransferCompanies from "../dialogs/Companies/TransferCompanies";
import { COMPANY_ACTIONS } from "../types";
import ContentFrameWithButton from "./components/ContentFrameWithButton";
import { useDialog, useMenu } from "./hooks";

const CompaniesBar = () => {
    const { rootCompanyLevel, rootCompanyType } = useContext(ParamsContext);
    const { isAdminUser, isSuperUser } = useContext(AuthContext);
    const { isInitialLoading } = useContext(TableContext);

    const { t } = useTranslation();
    const { toSm, toMd } = useMediaQueries();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();
    const { anchorEl, handleOpenMenu, handleCloseMenu } = useMenu();
    const { isCreateCompanyNotAllowed, PERMISSIONS } = usePermissions();

    const renderCompanySettingsBtn = () => {
        const isClientType = isClientTypeCompany(rootCompanyType);

        if (isSuperUser) {
            const isPremium = isPremiumLevel(rootCompanyLevel);

            if (isClientType && isPremium) {
                return renderIndividualButton(
                    t("Button##company settings"),
                    <ExpandMoreIcon />,
                    e => handleOpenMenu(e),
                    false
                );
            }

            if (isClientType) {
                return renderIndividualButton(
                    t("General##company level"),
                    <MonitoringLevelIcon />,
                    () => openDialog(COMPANY_ACTIONS.CompanyLevelManualChange)
                );
            }

            if (isPremium) {
                return renderIndividualButton(
                    t("General##custom resource limits"),
                    <TuneIcon />,
                    () => openDialog(COMPANY_ACTIONS.CustomResourceLimits)
                );
            }
        }

        if (isAdminUser && isClientType) {
            return renderIndividualButton(
                t("General##company level"),
                <MonitoringLevelIcon />,
                () => openDialog(COMPANY_ACTIONS.CompanyLevelManualChange)
            );
        }

        return null;
    };

    const renderIndividualButton = (
        title: string,
        icon: ReactElement,
        onClick: (e: { currentTarget: SetStateAction<MenuAnchorEl> }) => void,
        iconLeft: boolean = true
    ) => (
        <>
            {toMd ? (
                <Button
                    variant={BUTTON_VARIANTS.IconOnly}
                    size={BUTTON_SIZES.Small}
                    color={BUTTON_COLORS.White}
                    icon={icon}
                    onClick={onClick}
                />
            ) : (
                <Button
                    variant={
                        iconLeft
                            ? BUTTON_VARIANTS.IconLeft
                            : BUTTON_VARIANTS.IconRight
                    }
                    size={BUTTON_SIZES.Small}
                    color={BUTTON_COLORS.White}
                    icon={icon}
                    onClick={onClick}
                >
                    {title}
                </Button>
            )}

            {!iconLeft && (
                <Menu
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorEl={anchorEl}
                    anchorOrigin={centeredAnchorOrigin}
                    transformOrigin={centeredTransformOrigin}
                    css={css({
                        "& .MuiMenu-paper": {
                            minWidth: "211px"
                        }
                    })}
                >
                    <ListChoice
                        title={t("General##company level")}
                        iconLeft={<MonitoringLevelIcon />}
                        onClick={() => {
                            handleCloseMenu();
                            openDialog(
                                COMPANY_ACTIONS.CompanyLevelManualChange
                            );
                        }}
                    />

                    <ListChoice
                        title={t("General##custom resource limits")}
                        iconLeft={<TuneIcon />}
                        onClick={() => {
                            handleCloseMenu();
                            openDialog(COMPANY_ACTIONS.CustomResourceLimits);
                        }}
                    />
                </Menu>
            )}
        </>
    );

    return (
        <>
            <ContentFrameWithButton button={renderCompanySettingsBtn()}>
                <ActionToolbarButtonWrapper
                    permissionName={PERMISSIONS.CompanyCreate}
                >
                    <Button
                        fullWidth={toSm}
                        hasRectangleLoader={toSm && isInitialLoading}
                        variant={BUTTON_VARIANTS.TextOnly}
                        size={BUTTON_SIZES.Small}
                        color={BUTTON_COLORS.Primary}
                        disabled={isCreateCompanyNotAllowed}
                        onClick={() =>
                            openDialog(COMPANY_ACTIONS.CreateCompany)
                        }
                    >
                        {t("Button##create")}
                    </Button>
                </ActionToolbarButtonWrapper>

                <ActionToolbarButtonWrapper>
                    <Button
                        fullWidth={toSm}
                        hasRectangleLoader={toSm && isInitialLoading}
                        variant={BUTTON_VARIANTS.TextOnly}
                        size={BUTTON_SIZES.Small}
                        color={BUTTON_COLORS.White}
                        onClick={() =>
                            openDialog(COMPANY_ACTIONS.TransferCompanies)
                        }
                    >
                        {t("Button##transfer")}
                    </Button>
                </ActionToolbarButtonWrapper>
            </ContentFrameWithButton>

            <CreateCompany
                isOpen={isOpenDialog[COMPANY_ACTIONS.CreateCompany]}
                close={closeDialog}
            />

            <TransferCompanies
                isOpen={isOpenDialog[COMPANY_ACTIONS.TransferCompanies]}
                close={closeDialog}
            />

            <CompanyLevelManualChange
                isOpen={isOpenDialog[COMPANY_ACTIONS.CompanyLevelManualChange]}
                close={closeDialog}
            />

            <CustomResourceLimits
                isOpen={isOpenDialog[COMPANY_ACTIONS.CustomResourceLimits]}
                close={closeDialog}
            />
        </>
    );
};

export default CompaniesBar;
